import { Button } from '@/components/ui/button';
import { Pencil, Trash2 } from 'lucide-react';
import type { SupplementJustification } from '@/api/generated/client/models';
import type { LineItemCategory } from '@/types/supplement';
import { useSupplementCanWrite } from '../supplement-access-context';

interface JustificationFooterProps {
  justification: SupplementJustification | null | undefined;
  category: LineItemCategory;
  onEdit: () => void;
  onDelete?: () => void;
}

/**
 * Footer section displaying AI-generated justification with optional code book references
 * Shows edit and delete buttons for modifying justification text
 * Only allow adding justifications for unmatched_contractor and underscoped items
 */
export function JustificationFooter({ justification, category, onEdit, onDelete }: JustificationFooterProps) {
  const canWrite = useSupplementCanWrite();
  // Show add button for items that can have justifications (unmatched contractor and underscoped)
  const canHaveJustification = category === 'unmatched_contractor' || category === 'underscoped';
  
  // Check if justification exists and has actual content
  const hasJustificationContent = justification && (
    justification.justificationText ||
    justification.codeBookTitle ||
    justification.chapter ||
    justification.snippet
  );
  
  if (!hasJustificationContent && canHaveJustification) {
    // Read-only users have nothing to display for an empty justification.
    if (!canWrite) {
      return null;
    }
    return (
      <div className="border-t bg-muted/30 p-3 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={onEdit}
        >
          <Pencil className="h-3 w-3 mr-1" />
          Add Justification
        </Button>
      </div>
    );
  }

  // Don't render if no justification content
  if (!hasJustificationContent) {
    return null;
  }

  return (
    <div className="border-t bg-muted/30 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
          Justification
        </div>
        {canWrite && (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={onEdit}
          >
            <Pencil className="h-3 w-3 mr-1" />
            Edit
          </Button>
          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[10px] text-destructive hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Delete
            </Button>
          )}
        </div>
        )}
      </div>
      
      <p className="text-xs leading-relaxed text-foreground/80 mb-2">
        {justification.justificationText || 'No justification text provided.'}
      </p>

      {/* Code Book Reference Section */}
      {(justification.codeBookTitle || justification.chapter || justification.snippet) && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <div className="text-[10px] font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
            Code Book Reference
          </div>
          <div className="space-y-1.5 text-xs">
            {(justification.codeBookTitle || justification.chapter) && (
              <div>
                {justification.codeBookTitle && (
                  <>
                    <span className="font-medium text-muted-foreground">Title:</span>{' '}
                    <span className="text-foreground">{justification.codeBookTitle}</span>
                  </>
                )}
                {justification.codeBookTitle && justification.chapter && (
                  <span className="text-muted-foreground"> • </span>
                )}
                {justification.chapter && (
                  <>
                    <span className="font-medium text-muted-foreground">Chapter:</span>{' '}
                    <span className="text-foreground">{justification.chapter}</span>
                  </>
                )}
              </div>
            )}
            {justification.snippet && (
              <div className="mt-2 pt-2 border-t border-slate-100">
                <p className="text-[11px] italic text-foreground/70 pl-3 border-l-2 border-slate-300 leading-relaxed">
                  {justification.snippet}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
